import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  BOM, Component, Product,
  SearchBOMsRequest, SearchBOMsResponse,
  SearchComponentsRequest, SearchComponentsResponse,
  SearchProductsRequest, SearchProductsResponse,
  CreateBOMRequest, CreateComponentRequest, CreateProductRequest,
  BOMStructure
} from '../../services/bomApi';
import { WorkOrder } from '../../services/workOrderApi';

export interface BOMState {
  // BOM State
  boms: BOM[];
  selectedBOM: BOM | null;
  bomLoading: boolean;
  bomError: string | null;
  bomValidationErrors: string[];
  
  // Component State
  components: Component[];
  selectedComponent: Component | null;
  componentLoading: boolean;
  componentError: string | null;
  componentValidationErrors: string[];
  
  // Product State
  products: Product[];
  selectedProduct: Product | null;
  productLoading: boolean;
  productError: string | null;
  productValidationErrors: string[];
  
  // Work Order Integration
  workOrders: WorkOrder[];
  linkedWorkOrders: WorkOrder[];
  workOrderLoading: boolean;
  
  // BOM Structure Analysis
  bomStructure: BOMStructure | null;
  structureLoading: boolean;
  
  // Pagination and Filtering
  bomPagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  componentPagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  productPagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  
  // Search and Filters
  bomSearchTerm: string;
  componentSearchTerm: string;
  productSearchTerm: string;
  bomActiveFilter: boolean | null;
  componentActiveFilter: boolean | null;
  productActiveFilter: boolean | null;
  
  // Dropdown Data
  dropdownComponents: Component[];
  dropdownProducts: Product[];
  dropdownWorkOrders: WorkOrder[];
  dropdownsLoading: boolean;
  
  // UI State
  bomDialogOpen: boolean;
  componentDialogOpen: boolean;
  productDialogOpen: boolean;
  workOrderDialogOpen: boolean;
  editingMode: 'create' | 'edit' | null;
  selectedTab: number;
  
  // Export/Import
  exportLoading: boolean;
  exportError: string | null;
}

const initialState: BOMState = {
  // BOM State
  boms: [],
  selectedBOM: null,
  bomLoading: false,
  bomError: null,
  bomValidationErrors: [],
  
  // Component State
  components: [],
  selectedComponent: null,
  componentLoading: false,
  componentError: null,
  componentValidationErrors: [],
  
  // Product State
  products: [],
  selectedProduct: null,
  productLoading: false,
  productError: null,
  productValidationErrors: [],
  
  // Work Order Integration
  workOrders: [],
  linkedWorkOrders: [],
  workOrderLoading: false,
  
  // BOM Structure Analysis
  bomStructure: null,
  structureLoading: false,
  
  // Pagination
  bomPagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  },
  componentPagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  },
  productPagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  },
  
  // Search and Filters
  bomSearchTerm: '',
  componentSearchTerm: '',
  productSearchTerm: '',
  bomActiveFilter: null,
  componentActiveFilter: null,
  productActiveFilter: null,
  
  // Dropdown Data
  dropdownComponents: [],
  dropdownProducts: [],
  dropdownWorkOrders: [],
  dropdownsLoading: false,
  
  // UI State
  bomDialogOpen: false,
  componentDialogOpen: false,
  productDialogOpen: false,
  workOrderDialogOpen: false,
  editingMode: null,
  selectedTab: 0,
  
  // Export/Import
  exportLoading: false,
  exportError: null,
};

const bomSlice = createSlice({
  name: 'bom',
  initialState,
  reducers: {
    // BOM Actions
    fetchBOMsStart: (state, action: PayloadAction<SearchBOMsRequest | undefined>) => {
      state.bomLoading = true;
      state.bomError = null;
    },
    fetchBOMsSuccess: (state, action: PayloadAction<SearchBOMsResponse>) => {
      state.bomLoading = false;
      state.boms = action.payload.data;
      state.bomPagination = {
        currentPage: action.payload.pageNum,
        pageSize: action.payload.pageSize,
        totalCount: action.payload.totalCount,
        totalPages: Math.ceil(action.payload.totalCount / action.payload.pageSize),
      };
    },
    fetchBOMsFailure: (state, action: PayloadAction<string>) => {
      state.bomLoading = false;
      state.bomError = action.payload;
    },
    
    fetchBOMByIdStart: (state, action: PayloadAction<string>) => {
      state.bomLoading = true;
      state.bomError = null;
    },
    fetchBOMByIdSuccess: (state, action: PayloadAction<BOM>) => {
      state.bomLoading = false;
      state.selectedBOM = action.payload;
    },
    fetchBOMByIdFailure: (state, action: PayloadAction<string>) => {
      state.bomLoading = false;
      state.bomError = action.payload;
    },
    
    createBOMStart: (state, action: PayloadAction<CreateBOMRequest>) => {
      state.bomLoading = true;
      state.bomError = null;
      state.bomValidationErrors = [];
    },
    createBOMSuccess: (state, action: PayloadAction<BOM>) => {
      state.bomLoading = false;
      state.boms.unshift(action.payload);
      state.selectedBOM = action.payload;
      state.bomDialogOpen = false;
      state.editingMode = null;
    },
    createBOMFailure: (state, action: PayloadAction<string>) => {
      state.bomLoading = false;
      state.bomError = action.payload;
    },
    
    updateBOMStart: (state, action: PayloadAction<Partial<BOM>>) => {
      state.bomLoading = true;
      state.bomError = null;
      state.bomValidationErrors = [];
    },
    updateBOMSuccess: (state, action: PayloadAction<BOM>) => {
      state.bomLoading = false;
      const index = state.boms.findIndex(bom => bom.id === action.payload.id);
      if (index !== -1) {
        state.boms[index] = action.payload;
      }
      state.selectedBOM = action.payload;
      state.bomDialogOpen = false;
      state.editingMode = null;
    },
    updateBOMFailure: (state, action: PayloadAction<string>) => {
      state.bomLoading = false;
      state.bomError = action.payload;
    },
    
    deleteBOMStart: (state, action: PayloadAction<string>) => {
      state.bomLoading = true;
      state.bomError = null;
    },
    deleteBOMSuccess: (state, action: PayloadAction<string>) => {
      state.bomLoading = false;
      state.boms = state.boms.filter(bom => bom.id !== action.payload);
      if (state.selectedBOM?.id === action.payload) {
        state.selectedBOM = null;
      }
    },
    deleteBOMFailure: (state, action: PayloadAction<string>) => {
      state.bomLoading = false;
      state.bomError = action.payload;
    },
    
    // Component Actions
    fetchComponentsStart: (state, action: PayloadAction<SearchComponentsRequest | undefined>) => {
      state.componentLoading = true;
      state.componentError = null;
    },
    fetchComponentsSuccess: (state, action: PayloadAction<SearchComponentsResponse>) => {
      state.componentLoading = false;
      state.components = action.payload.data;
      state.componentPagination = {
        currentPage: action.payload.pageNum,
        pageSize: action.payload.pageSize,
        totalCount: action.payload.totalCount,
        totalPages: Math.ceil(action.payload.totalCount / action.payload.pageSize),
      };
    },
    fetchComponentsFailure: (state, action: PayloadAction<string>) => {
      state.componentLoading = false;
      state.componentError = action.payload;
    },
    
    fetchComponentByIdStart: (state, action: PayloadAction<string>) => {
      state.componentLoading = true;
      state.componentError = null;
    },
    fetchComponentByIdSuccess: (state, action: PayloadAction<Component>) => {
      state.componentLoading = false;
      state.selectedComponent = action.payload;
    },
    fetchComponentByIdFailure: (state, action: PayloadAction<string>) => {
      state.componentLoading = false;
      state.componentError = action.payload;
    },
    
    createComponentStart: (state, action: PayloadAction<CreateComponentRequest>) => {
      state.componentLoading = true;
      state.componentError = null;
      state.componentValidationErrors = [];
    },
    createComponentSuccess: (state, action: PayloadAction<Component>) => {
      state.componentLoading = false;
      state.components.unshift(action.payload);
      state.selectedComponent = action.payload;
      state.componentDialogOpen = false;
      state.editingMode = null;
    },
    createComponentFailure: (state, action: PayloadAction<string>) => {
      state.componentLoading = false;
      state.componentError = action.payload;
    },
    
    updateComponentStart: (state, action: PayloadAction<Partial<Component>>) => {
      state.componentLoading = true;
      state.componentError = null;
      state.componentValidationErrors = [];
    },
    updateComponentSuccess: (state, action: PayloadAction<Component>) => {
      state.componentLoading = false;
      const index = state.components.findIndex(comp => comp.id === action.payload.id);
      if (index !== -1) {
        state.components[index] = action.payload;
      }
      state.selectedComponent = action.payload;
      state.componentDialogOpen = false;
      state.editingMode = null;
    },
    updateComponentFailure: (state, action: PayloadAction<string>) => {
      state.componentLoading = false;
      state.componentError = action.payload;
    },
    
    deleteComponentStart: (state, action: PayloadAction<string>) => {
      state.componentLoading = true;
      state.componentError = null;
    },
    deleteComponentSuccess: (state, action: PayloadAction<string>) => {
      state.componentLoading = false;
      state.components = state.components.filter(comp => comp.id !== action.payload);
      if (state.selectedComponent?.id === action.payload) {
        state.selectedComponent = null;
      }
    },
    deleteComponentFailure: (state, action: PayloadAction<string>) => {
      state.componentLoading = false;
      state.componentError = action.payload;
    },
    
    // Product Actions
    fetchProductsStart: (state, action: PayloadAction<SearchProductsRequest | undefined>) => {
      state.productLoading = true;
      state.productError = null;
    },
    fetchProductsSuccess: (state, action: PayloadAction<SearchProductsResponse>) => {
      state.productLoading = false;
      state.products = action.payload.data;
      state.productPagination = {
        currentPage: action.payload.pageNum,
        pageSize: action.payload.pageSize,
        totalCount: action.payload.totalCount,
        totalPages: Math.ceil(action.payload.totalCount / action.payload.pageSize),
      };
    },
    fetchProductsFailure: (state, action: PayloadAction<string>) => {
      state.productLoading = false;
      state.productError = action.payload;
    },
    
    fetchProductByIdStart: (state, action: PayloadAction<string>) => {
      state.productLoading = true;
      state.productError = null;
    },
    fetchProductByIdSuccess: (state, action: PayloadAction<Product>) => {
      state.productLoading = false;
      state.selectedProduct = action.payload;
    },
    fetchProductByIdFailure: (state, action: PayloadAction<string>) => {
      state.productLoading = false;
      state.productError = action.payload;
    },
    
    createProductStart: (state, action: PayloadAction<CreateProductRequest>) => {
      state.productLoading = true;
      state.productError = null;
      state.productValidationErrors = [];
    },
    createProductSuccess: (state, action: PayloadAction<Product>) => {
      state.productLoading = false;
      state.products.unshift(action.payload);
      state.selectedProduct = action.payload;
      state.productDialogOpen = false;
      state.editingMode = null;
    },
    createProductFailure: (state, action: PayloadAction<string>) => {
      state.productLoading = false;
      state.productError = action.payload;
    },
    
    updateProductStart: (state, action: PayloadAction<Partial<Product>>) => {
      state.productLoading = true;
      state.productError = null;
      state.productValidationErrors = [];
    },
    updateProductSuccess: (state, action: PayloadAction<Product>) => {
      state.productLoading = false;
      const index = state.products.findIndex(prod => prod.id === action.payload.id);
      if (index !== -1) {
        state.products[index] = action.payload;
      }
      state.selectedProduct = action.payload;
      state.productDialogOpen = false;
      state.editingMode = null;
    },
    updateProductFailure: (state, action: PayloadAction<string>) => {
      state.productLoading = false;
      state.productError = action.payload;
    },
    
    deleteProductStart: (state, action: PayloadAction<string>) => {
      state.productLoading = true;
      state.productError = null;
    },
    deleteProductSuccess: (state, action: PayloadAction<string>) => {
      state.productLoading = false;
      state.products = state.products.filter(prod => prod.id !== action.payload);
      if (state.selectedProduct?.id === action.payload) {
        state.selectedProduct = null;
      }
    },
    deleteProductFailure: (state, action: PayloadAction<string>) => {
      state.productLoading = false;
      state.productError = action.payload;
    },
    
    // Work Order Actions
    fetchWorkOrdersStart: (state, action: PayloadAction<string>) => {
      state.workOrderLoading = true;
    },
    fetchWorkOrdersSuccess: (state, action: PayloadAction<WorkOrder[]>) => {
      state.workOrderLoading = false;
      state.linkedWorkOrders = action.payload;
    },
    fetchWorkOrdersFailure: (state, action: PayloadAction<string>) => {
      state.workOrderLoading = false;
      state.productError = action.payload;
    },
    
    linkProductToWorkOrderStart: (state, action: PayloadAction<{productId: string, workOrderId: string}>) => {
      state.workOrderLoading = true;
    },
    linkProductToWorkOrderSuccess: (state, action: PayloadAction<WorkOrder>) => {
      state.workOrderLoading = false;
      state.linkedWorkOrders.push(action.payload);
    },
    linkProductToWorkOrderFailure: (state, action: PayloadAction<string>) => {
      state.workOrderLoading = false;
      state.productError = action.payload;
    },
    
    unlinkProductFromWorkOrderStart: (state, action: PayloadAction<{productId: string, workOrderId: string}>) => {
      state.workOrderLoading = true;
    },
    unlinkProductFromWorkOrderSuccess: (state, action: PayloadAction<string>) => {
      state.workOrderLoading = false;
      state.linkedWorkOrders = state.linkedWorkOrders.filter(wo => wo.id !== action.payload);
    },
    unlinkProductFromWorkOrderFailure: (state, action: PayloadAction<string>) => {
      state.workOrderLoading = false;
      state.productError = action.payload;
    },
    
    // BOM Structure Actions
    fetchBOMStructureStart: (state, action: PayloadAction<string>) => {
      state.structureLoading = true;
    },
    fetchBOMStructureSuccess: (state, action: PayloadAction<BOMStructure>) => {
      state.structureLoading = false;
      state.bomStructure = action.payload;
    },
    fetchBOMStructureFailure: (state, action: PayloadAction<string>) => {
      state.structureLoading = false;
      state.bomError = action.payload;
    },
    
    // Dropdown Data Actions
    fetchDropdownsStart: (state) => {
      state.dropdownsLoading = true;
    },
    fetchDropdownsSuccess: (state, action: PayloadAction<{
      components: Component[];
      products: Product[];
      workOrders: WorkOrder[];
    }>) => {
      state.dropdownsLoading = false;
      state.dropdownComponents = action.payload.components;
      state.dropdownProducts = action.payload.products;
      state.dropdownWorkOrders = action.payload.workOrders;
    },
    fetchDropdownsFailure: (state, action: PayloadAction<string>) => {
      state.dropdownsLoading = false;
      state.bomError = action.payload;
    },
    
    // Search and Filter Actions
    setBOMSearchTerm: (state, action: PayloadAction<string>) => {
      state.bomSearchTerm = action.payload;
      state.bomPagination.currentPage = 1;
    },
    setComponentSearchTerm: (state, action: PayloadAction<string>) => {
      state.componentSearchTerm = action.payload;
      state.componentPagination.currentPage = 1;
    },
    setProductSearchTerm: (state, action: PayloadAction<string>) => {
      state.productSearchTerm = action.payload;
      state.productPagination.currentPage = 1;
    },
    
    setBOMActiveFilter: (state, action: PayloadAction<boolean | null>) => {
      state.bomActiveFilter = action.payload;
      state.bomPagination.currentPage = 1;
    },
    setComponentActiveFilter: (state, action: PayloadAction<boolean | null>) => {
      state.componentActiveFilter = action.payload;
      state.componentPagination.currentPage = 1;
    },
    setProductActiveFilter: (state, action: PayloadAction<boolean | null>) => {
      state.productActiveFilter = action.payload;
      state.productPagination.currentPage = 1;
    },
    
    // Pagination Actions
    setBOMPage: (state, action: PayloadAction<number>) => {
      state.bomPagination.currentPage = action.payload;
    },
    setComponentPage: (state, action: PayloadAction<number>) => {
      state.componentPagination.currentPage = action.payload;
    },
    setProductPage: (state, action: PayloadAction<number>) => {
      state.productPagination.currentPage = action.payload;
    },
    
    setBOMPageSize: (state, action: PayloadAction<number>) => {
      state.bomPagination.pageSize = action.payload;
      state.bomPagination.currentPage = 1;
    },
    setComponentPageSize: (state, action: PayloadAction<number>) => {
      state.componentPagination.pageSize = action.payload;
      state.componentPagination.currentPage = 1;
    },
    setProductPageSize: (state, action: PayloadAction<number>) => {
      state.productPagination.pageSize = action.payload;
      state.productPagination.currentPage = 1;
    },
    
    // UI Actions
    openBOMDialog: (state, action: PayloadAction<{ mode: 'create' | 'edit'; bom?: BOM }>) => {
      state.bomDialogOpen = true;
      state.editingMode = action.payload.mode;
      state.selectedBOM = action.payload.bom || null;
      state.bomValidationErrors = [];
    },
    closeBOMDialog: (state) => {
      state.bomDialogOpen = false;
      state.editingMode = null;
      state.selectedBOM = null;
      state.bomValidationErrors = [];
    },
    
    openComponentDialog: (state, action: PayloadAction<{ mode: 'create' | 'edit'; component?: Component }>) => {
      state.componentDialogOpen = true;
      state.editingMode = action.payload.mode;
      state.selectedComponent = action.payload.component || null;
      state.componentValidationErrors = [];
    },
    closeComponentDialog: (state) => {
      state.componentDialogOpen = false;
      state.editingMode = null;
      state.selectedComponent = null;
      state.componentValidationErrors = [];
    },
    
    openProductDialog: (state, action: PayloadAction<{ mode: 'create' | 'edit'; product?: Product }>) => {
      state.productDialogOpen = true;
      state.editingMode = action.payload.mode;
      state.selectedProduct = action.payload.product || null;
      state.productValidationErrors = [];
    },
    closeProductDialog: (state) => {
      state.productDialogOpen = false;
      state.editingMode = null;
      state.selectedProduct = null;
      state.productValidationErrors = [];
    },
    
    openWorkOrderDialog: (state) => {
      state.workOrderDialogOpen = true;
    },
    closeWorkOrderDialog: (state) => {
      state.workOrderDialogOpen = false;
    },
    
    setSelectedTab: (state, action: PayloadAction<number>) => {
      state.selectedTab = action.payload;
    },
    
    // Validation Actions
    setBOMValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.bomValidationErrors = action.payload;
    },
    setComponentValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.componentValidationErrors = action.payload;
    },
    setProductValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.productValidationErrors = action.payload;
    },
    
    clearBOMValidationErrors: (state) => {
      state.bomValidationErrors = [];
    },
    clearComponentValidationErrors: (state) => {
      state.componentValidationErrors = [];
    },
    clearProductValidationErrors: (state) => {
      state.productValidationErrors = [];
    },
    
    // Error Management
    clearBOMError: (state) => {
      state.bomError = null;
    },
    clearComponentError: (state) => {
      state.componentError = null;
    },
    clearProductError: (state) => {
      state.productError = null;
    },
    clearExportError: (state) => {
      state.exportError = null;
    },
    
    // Export Actions
    exportBOMStart: (state) => {
      state.exportLoading = true;
      state.exportError = null;
    },
    exportBOMSuccess: (state) => {
      state.exportLoading = false;
    },
    exportBOMFailure: (state, action: PayloadAction<string>) => {
      state.exportLoading = false;
      state.exportError = action.payload;
    },
  },
});

export const {
  // BOM Actions
  fetchBOMsStart, fetchBOMsSuccess, fetchBOMsFailure,
  fetchBOMByIdStart, fetchBOMByIdSuccess, fetchBOMByIdFailure,
  createBOMStart, createBOMSuccess, createBOMFailure,
  updateBOMStart, updateBOMSuccess, updateBOMFailure,
  deleteBOMStart, deleteBOMSuccess, deleteBOMFailure,
  
  // Component Actions
  fetchComponentsStart, fetchComponentsSuccess, fetchComponentsFailure,
  fetchComponentByIdStart, fetchComponentByIdSuccess, fetchComponentByIdFailure,
  createComponentStart, createComponentSuccess, createComponentFailure,
  updateComponentStart, updateComponentSuccess, updateComponentFailure,
  deleteComponentStart, deleteComponentSuccess, deleteComponentFailure,
  
  // Product Actions
  fetchProductsStart, fetchProductsSuccess, fetchProductsFailure,
  fetchProductByIdStart, fetchProductByIdSuccess, fetchProductByIdFailure,
  createProductStart, createProductSuccess, createProductFailure,
  updateProductStart, updateProductSuccess, updateProductFailure,
  deleteProductStart, deleteProductSuccess, deleteProductFailure,
  
  // Work Order Actions
  fetchWorkOrdersStart, fetchWorkOrdersSuccess, fetchWorkOrdersFailure,
  linkProductToWorkOrderStart, linkProductToWorkOrderSuccess, linkProductToWorkOrderFailure,
  unlinkProductFromWorkOrderStart, unlinkProductFromWorkOrderSuccess, unlinkProductFromWorkOrderFailure,
  
  // BOM Structure Actions
  fetchBOMStructureStart, fetchBOMStructureSuccess, fetchBOMStructureFailure,
  
  // Dropdown Actions
  fetchDropdownsStart, fetchDropdownsSuccess, fetchDropdownsFailure,
  
  // Search and Filter Actions
  setBOMSearchTerm, setComponentSearchTerm, setProductSearchTerm,
  setBOMActiveFilter, setComponentActiveFilter, setProductActiveFilter,
  
  // Pagination Actions
  setBOMPage, setComponentPage, setProductPage,
  setBOMPageSize, setComponentPageSize, setProductPageSize,
  
  // UI Actions
  openBOMDialog, closeBOMDialog,
  openComponentDialog, closeComponentDialog,
  openProductDialog, closeProductDialog,
  openWorkOrderDialog, closeWorkOrderDialog,
  setSelectedTab,
  
  // Validation Actions
  setBOMValidationErrors, setComponentValidationErrors, setProductValidationErrors,
  clearBOMValidationErrors, clearComponentValidationErrors, clearProductValidationErrors,
  
  // Error Management
  clearBOMError, clearComponentError, clearProductError, clearExportError,
  
  // Export Actions
  exportBOMStart, exportBOMSuccess, exportBOMFailure,
} = bomSlice.actions;

export default bomSlice.reducer; 